import React, { useEffect, useState, useCallback } from 'react';
import { FiCopy } from 'react-icons/fi';
import api from '../../services/api';
import TotpSetupQrBlock from '../TotpSetupQrBlock';

async function copyTextToClipboard(text) {
  const t = String(text || '');
  if (!t) return false;
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(t);
      return true;
    } catch {
      /* fallback below */
    }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = t;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    ta.style.top = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, t.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export default function TwoFactorSettingsModal({ open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [copyHint, setCopyHint] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);

  const [setupSecret, setSetupSecret] = useState('');
  const [setupOtpauth, setSetupOtpauth] = useState('');
  const [enableCode, setEnableCode] = useState('');

  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/api/me/2fa/status');
      setEnabled(!!data.enabled);
      return !!data.enabled;
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Не удалось загрузить статус');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const startSetup = useCallback(async () => {
    setError('');
    setInfo('');
    setSetupLoading(true);
    try {
      const { data } = await api.post('/api/me/2fa/setup');
      setSetupSecret(data.secret || '');
      setSetupOtpauth(data.otpauthUrl || '');
      setEnableCode('');
      if (!data.otpauthUrl) {
        setError('Сервер не вернул ссылку для QR. Используйте секретный ключ вручную.');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Ошибка подготовки QR');
    } finally {
      setSetupLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSetupSecret('');
    setSetupOtpauth('');
    setEnableCode('');
    setDisablePassword('');
    setDisableCode('');
    setInfo('');
    setCopyHint('');
    setShowDisable(false);
    (async () => {
      const isEnabled = await loadStatus();
      if (cancelled) return;
      // Всегда сразу готовим QR: и для первой привязки, и для перепривязки
      await startSetup();
    })();
    return () => {
      cancelled = true;
    };
  }, [open, startSetup]);

  const copySecret = useCallback(async () => {
    setError('');
    setCopyHint('');
    const ok = await copyTextToClipboard(setupSecret);
    if (ok) {
      setCopyHint('Секрет скопирован — вставьте в приложение-аутентификатор');
      window.setTimeout(() => setCopyHint(''), 3500);
    } else {
      setError('Не удалось скопировать автоматически. Выделите ключ вручную.');
    }
  }, [setupSecret]);

  const submitEnable = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!setupSecret || !enableCode.trim()) {
      setError('Введите код из приложения');
      return;
    }
    try {
      await api.post('/api/me/2fa/enable', { secret: setupSecret, code: enableCode.replace(/\s/g, '') });
      setEnabled(true);
      setSetupSecret('');
      setSetupOtpauth('');
      setEnableCode('');
      setInfo(
        enabled
          ? 'Google Authenticator перепривязан. Старый код больше не действует.'
          : 'Двухфакторная аутентификация включена.'
      );
      await loadStatus();
      window.dispatchEvent(new Event('mesendger:2fa-changed'));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Ошибка');
    }
  };

  const submitDisable = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!disablePassword) {
      setError('Введите пароль');
      return;
    }
    try {
      await api.post('/api/me/2fa/disable', {
        password: disablePassword,
        code: disableCode.replace(/\s/g, '') || undefined
      });
      setEnabled(false);
      setDisablePassword('');
      setDisableCode('');
      setShowDisable(false);
      setInfo('2FA отключена.');
      await loadStatus();
      await startSetup();
      window.dispatchEvent(new Event('mesendger:2fa-changed'));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Ошибка');
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        padding: 12,
        boxSizing: 'border-box',
        overflow: 'auto'
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(460px, 96vw)',
          maxHeight: 'min(92vh, 900px)',
          overflowY: 'auto',
          background: 'linear-gradient(135deg, #1e293b 0%, #020617 100%)',
          borderRadius: 18,
          padding: 24,
          color: '#e5e7eb',
          boxShadow: '0 20px 45px rgba(0,0,0,0.65)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Двухфакторный вход (TOTP)</h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#9ca3af',
              fontSize: 22,
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>

        <p style={{ marginTop: 0, marginBottom: 14, fontSize: 14, color: '#9ca3af', lineHeight: 1.45 }}>
          Отсканируйте QR-код в Google Authenticator (или Microsoft Authenticator) с телефона.
          Если открываете сайт на том же телефоне — введите секретный ключ вручную в приложении.
        </p>

        {loading && <div style={{ color: '#94a3b8' }}>Загрузка…</div>}

        {!loading && (
          <>
            {error && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#f97373' }}>{error}</div>
            )}
            {info && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#4ade80' }}>{info}</div>
            )}

            {enabled && !setupSecret && (
              <div
                style={{
                  marginBottom: 14,
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: 'rgba(34,197,94,0.12)',
                  border: '1px solid rgba(74,222,128,0.35)',
                  color: '#86efac',
                  fontSize: 13
                }}
              >
                2FA уже включена. Ниже можно показать новый QR и перепривязать приложение.
              </div>
            )}

            {setupLoading && !setupSecret && (
              <div style={{ color: '#94a3b8', marginBottom: 12 }}>Готовим QR-код…</div>
            )}

            {setupSecret && (
              <form onSubmit={submitEnable} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <TotpSetupQrBlock
                  value={setupOtpauth}
                  size={240}
                  caption="Откройте Google Authenticator → «+» → «Сканировать QR-код» и наведите камеру на код ниже (лучше с другого устройства)."
                />

                {!setupOtpauth && (
                  <div style={{ fontSize: 13, color: '#fbbf24' }}>
                    QR недоступен — добавьте аккаунт вручную по секретному ключу ниже.
                  </div>
                )}

                {setupOtpauth && (
                  <a
                    href={setupOtpauth}
                    style={{ color: '#38bdf8', fontSize: 14, textAlign: 'center' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Открыть ссылку otpauth (если приложение на этом же телефоне)
                  </a>
                )}

                <div>
                  <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 6 }}>
                    Секретный ключ (ручной ввод в Authenticator):
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      gap: 10,
                      alignItems: 'stretch'
                    }}
                  >
                    <code
                      style={{
                        flex: '1 1 180px',
                        display: 'block',
                        padding: 10,
                        borderRadius: 8,
                        background: '#0f172a',
                        color: '#a5f3fc',
                        fontSize: 13,
                        wordBreak: 'break-all',
                        margin: 0,
                        boxSizing: 'border-box',
                        userSelect: 'all',
                        WebkitUserSelect: 'all'
                      }}
                    >
                      {setupSecret}
                    </code>
                    <button
                      type="button"
                      onClick={copySecret}
                      style={{
                        flex: '0 0 auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        padding: '10px 14px',
                        borderRadius: 10,
                        border: '1px solid rgba(56,189,248,0.45)',
                        background: 'rgba(56,189,248,0.12)',
                        color: '#7dd3fc',
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        minHeight: 44,
                        WebkitTapHighlightColor: 'transparent'
                      }}
                    >
                      <FiCopy size={18} aria-hidden />
                      Копировать ключ
                    </button>
                  </div>
                  {copyHint ? (
                    <div style={{ marginTop: 8, fontSize: 13, color: '#4ade80' }}>{copyHint}</div>
                  ) : null}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#9ca3af', marginBottom: 4 }}>
                    Код из Google Authenticator
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={enableCode}
                    onChange={(e) => setEnableCode(e.target.value)}
                    placeholder="000000"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: '1px solid #334155',
                      background: '#020617',
                      color: '#e5e7eb',
                      fontSize: 16,
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <button
                  type="submit"
                  style={{
                    padding: '12px 16px',
                    borderRadius: 999,
                    border: 'none',
                    background: 'linear-gradient(90deg, #22c55e 0%, #4ade80 100%)',
                    color: '#0b1120',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 15
                  }}
                >
                  {enabled ? 'Подтвердить и перепривязать' : 'Подтвердить и включить 2FA'}
                </button>

                <button
                  type="button"
                  onClick={startSetup}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 999,
                    border: '1px solid #475569',
                    background: 'transparent',
                    color: '#cbd5e1',
                    cursor: 'pointer',
                    fontSize: 13
                  }}
                >
                  Показать другой QR-код
                </button>
              </form>
            )}

            {enabled && (
              <div style={{ marginTop: 18, borderTop: '1px solid #334155', paddingTop: 14 }}>
                {!showDisable ? (
                  <button
                    type="button"
                    onClick={() => setShowDisable(true)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 999,
                      border: '1px solid #fb923c',
                      background: 'transparent',
                      color: '#fdba74',
                      cursor: 'pointer',
                      fontSize: 13
                    }}
                  >
                    Отключить 2FA (только админ)
                  </button>
                ) : (
                  <form onSubmit={submitDisable} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, color: '#9ca3af', marginBottom: 4 }}>
                        Текущий пароль
                      </label>
                      <input
                        type="password"
                        value={disablePassword}
                        onChange={(e) => setDisablePassword(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: 10,
                          border: '1px solid #334155',
                          background: '#020617',
                          color: '#e5e7eb',
                          fontSize: 14,
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, color: '#9ca3af', marginBottom: 4 }}>
                        Код из приложения (рекомендуется)
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={disableCode}
                        onChange={(e) => setDisableCode(e.target.value)}
                        placeholder="000000"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: 10,
                          border: '1px solid #334155',
                          background: '#020617',
                          color: '#e5e7eb',
                          fontSize: 14,
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <button
                      type="submit"
                      style={{
                        padding: '10px 16px',
                        borderRadius: 999,
                        border: 'none',
                        background: 'linear-gradient(90deg, #f97316 0%, #fb923c 100%)',
                        color: '#0b1120',
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: 14
                      }}
                    >
                      Отключить 2FA
                    </button>
                  </form>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
